import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import overviewVideo from '../../../assets/video/touchpose_overview.mp4';
import VideoPlayer from './VideoPlayer';

const SectionCard: React.FC<{
  title: string;
  description: string;
  to?: string;
  comingSoon?: boolean;
  links?: { label: string; to: string }[];
}> = ({ title, description, to, comingSoon, links = [] }) => (
  <div className={`border border-surface-hover rounded-[12px] p-[24px] flex flex-col gap-[12px] ${comingSoon ? 'bg-surface-subtle opacity-60' : 'bg-surface opacity-100'}`}>
    <div>
      {to && !comingSoon ? (
        <Link to={to} className="no-underline">
          <h3 className="font-display text-[16px] font-bold text-content-primary mb-[6px] tracking-[-0.2px]">
            {title}
          </h3>
        </Link>
      ) : (
        <h3 className="font-display text-[16px] font-bold text-content-primary mb-[6px] tracking-[-0.2px]">
          {title}
          {comingSoon && (
            <span className="text-[11px] font-semibold text-content-secondary tracking-[0.05em] uppercase ml-[8px]">
              {comingSoon}
            </span>
          )}
        </h3>
      )}
      <p className="m-0 text-[13px] text-slate-500 leading-[1.6]">
        {description}
      </p>
    </div>

    {!comingSoon && links.length > 0 && (
      <div className="border-t border-surface pt-[12px] flex flex-wrap gap-[8px]">
        {links.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className="text-[12px] font-medium text-blue-link bg-surface border border-border-brand rounded-[6px] px-[10px] py-[4px] no-underline"
          >
            {link.label}
          </Link>
        ))}
      </div>
    )}
  </div>
);

const AudienceHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="font-display text-[14px] font-bold text-slate-400 tracking-[0.06em] uppercase mt-[32px] mb-[12px]">
    {children}
  </h2>
);

const DocsLanding: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary max-w-[720px]">
      {/* -- Header -- */}
      <h1 className="font-display text-[30px] font-extrabold text-content-primary mb-[24px] tracking-[-0.6px]">
        {t('docs.landing.title')}
      </h1>
      {/* -- Video -- */}
      <VideoPlayer src={overviewVideo} defaultVolume={0.25} />

      {/* -- Getting Started -- */}
      <SectionCard
        title={t('docs.landing.gettingStartedTitle')}
        description={t('docs.landing.gettingStartedDesc')}
        to="/touchpose/docs/getting-started"
        links={[
          {
            label: t('docs.landing.installationGuide'),
            to: '/touchpose/docs/getting-started'
          }
        ]}
      />

      {/* -- For Animators -- */}
      <AudienceHeading>{t('docs.landing.forAnimators')}</AudienceHeading>
      <div className="flex flex-col gap-[12px]">
        <SectionCard
          title={t('docs.landing.animatingTitle')}
          description={t('docs.landing.animatingDesc')}
          to="/touchpose/docs/animating"
          links={[
            {
              label: t('docs.landing.enabling'),
              to: '/touchpose/docs/animating#enabling'
            },
            {
              label: t('docs.landing.theOverlay'),
              to: '/touchpose/docs/animating#the-overlay'
            },
            {
              label: t('docs.landing.settings'),
              to: '/touchpose/docs/animating#settings'
            },
            {
              label: t('docs.landing.referencedScenes'),
              to: '/touchpose/docs/animating#referenced-scenes'
            }
          ]}
        />
        <SectionCard
          title={t('docs.landing.drawingPosesTitle')}
          description={t('docs.landing.drawingPosesDesc')}
          to="/touchpose/docs/drawing-poses"
          links={[
            {
              label: t('docs.landing.drawingPosesActivating'),
              to: '/touchpose/docs/drawing-poses#activating'
            },
            {
              label: t('docs.landing.drawingPosesSketch'),
              to: '/touchpose/docs/drawing-poses#sketch-and-pose'
            },
            {
              label: t('docs.landing.drawingPosesHotkeys'),
              to: '/touchpose/docs/drawing-poses#hotkeys'
            }
          ]}
        />
      </div>

      {/* -- For TDs -- */}
      <AudienceHeading>{t('docs.landing.forTDs')}</AudienceHeading>
      <div className="flex flex-col gap-[12px] mb-[48px]">
        <SectionCard
          title={t('docs.landing.setupTitle')}
          description={t('docs.landing.setupDesc')}
          to="/touchpose/docs/editor"
          links={[
            { label: t('docs.landing.menuAndEditor'), to: '/touchpose/docs/editor' },
            {
              label: t('docs.landing.importExportTouch'),
              to: '/touchpose/docs/loading-touchsets'
            },
            { label: t('docs.landing.actions'), to: '/touchpose/docs/actions' },
            {
              label: t('docs.landing.actionMenus'),
              to: '/touchpose/docs/actions-menus'
            }
          ]}
        />
        <SectionCard
          title={t('docs.landing.pythonApiTitle')}
          description={t('docs.landing.pythonApiDesc')}
          to="/touchpose/docs/api"
          links={[
            {
              label: t('docs.landing.exportImport'),
              to: '/touchpose/docs/api#export'
            },
            {
              label: t('docs.landing.touchSets'),
              to: '/touchpose/docs/api#touch-sets'
            },
            {
              label: t('docs.landing.addRemoveFaces'),
              to: '/touchpose/docs/api#add-remove-faces'
            },
            { label: t('docs.landing.actions'), to: '/touchpose/docs/api#actions' },
            {
              label: t('docs.landing.renameControl'),
              to: '/touchpose/docs/api#rename-control'
            },
            {
              label: t('docs.landing.setActiveLayer'),
              to: '/touchpose/docs/api#set-active-layer'
            }
          ]}
        />
      </div>

      {/* -- Support -- */}
      <div className="border-t border-surface-hover pt-[28px]">
        <p className="mb-[4px] text-[13px] font-semibold text-content-primary">
          {t('docs.landing.needHelp')}
        </p>
        <p className="m-0 text-[13px] text-slate-500 leading-[1.6]">
          <Trans
            i18nKey="docs.landing.needHelpText"
            components={{
              faqLink: (
                <Link
                  to="/touchpose/faq"
                  className="text-blue-link no-underline font-medium"
                />
              ),
              contactLink: (
                <Link
                  to="/contact"
                  className="text-blue-link no-underline font-medium"
                />
              )
            }}
          />
        </p>
      </div>
    </article>
  );
};

export default DocsLanding;
